import { Link } from 'react-router';
import { formatEuro, type PublicOrderView } from '@avh/shared';
import { OtpLogin } from '@/components/OtpLogin';
import { Container } from '@/components/layout/PublicLayout';
import { ApiStateView } from '@/components/ui/ApiStateView';
import { Button } from '@/components/ui/Button';
import { buttonClasses } from '@/components/ui/buttonClasses';
import { Card, PageHeading, StatusBadge } from '@/components/ui/Feedback';
import { useI18n } from '@/contexts/I18n';
import { useSession } from '@/contexts/Session';
import { formatDateTime, localized } from '@/lib/format';
import { useApi } from '@/lib/useApi';

function OrderRow({ order }: { order: PublicOrderView }) {
  const { t, locale } = useI18n();
  const isFree = order.totalCents === 0;
  return (
    <li className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="font-semibold text-brand-900">{localized(locale, order.event.title, order.event.titleEn)}</p>
        <p className="text-sm text-muted">
          {formatDateTime(order.event.startsAt, locale)} · {order.quantity} × · {isFree ? t.event.free : formatEuro(order.totalCents, locale)}
        </p>
        <div className="mt-1.5">
          <StatusBadge status={order.status}>
            {isFree && order.status === 'paid' ? t.order.statusFree : t.order.status[order.status]}
          </StatusBadge>
        </div>
      </div>
      <Link to={`/tickets/bestellung/${order.id}?k=${encodeURIComponent(order.accessKey)}`} className={buttonClasses('secondary', 'sm')}>
        {t.account.open}
      </Link>
    </li>
  );
}

function MyOrders({ token, email, onSignOut }: { token: string; email: string; onSignOut: () => void }) {
  const { t } = useI18n();
  const [state, reload] = useApi<PublicOrderView[]>('/account/orders', token);
  return (
    <Card>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">{t.account.signedInAs(email)}</p>
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          {t.account.signOut}
        </Button>
      </div>
      <ApiStateView state={state} onRetry={reload} loadingLabel={t.common.loading} retryLabel={t.common.retry}>
        {(orders) =>
          orders.length === 0 ? (
            <p className="py-4 text-muted">{t.account.none}</p>
          ) : (
            <ul className="divide-y divide-neutral-200">
              {orders.map((order) => (
                <OrderRow key={order.id} order={order} />
              ))}
            </ul>
          )
        }
      </ApiStateView>
    </Card>
  );
}

export function AccountPage() {
  const { t, locale } = useI18n();
  const { session, signIn, signOut } = useSession();
  return (
    <Container className="max-w-3xl">
      <PageHeading title={t.account.title}>{session ? null : t.account.intro}</PageHeading>
      {session ? (
        <MyOrders token={session.token} email={session.email} onSignOut={signOut} />
      ) : (
        <Card className="max-w-md">
          <OtpLogin texts={t.login} locale={locale} onSuccess={signIn} />
        </Card>
      )}
    </Container>
  );
}
